/**
 * PerChildContractorReportPreview · iter303ak-fix24 — Accepted · T30 · Action 30
 * ─────────────────────────────────────────────────────────────────────────────
 * Accordion — one PDF preview per (accepted child × contractor type).
 * Uses /api/appraisals/{child_id}/contractor-overview to find each
 * child's active contractor sections, then embeds an iframe for each
 * contractor's PDF via /api/appraisals/{child_id}/contractor-pdf/{ctype}?inline=1.
 *
 * Per-item "Include in send" toggle (default ON) — operator can uncheck
 * any (child, ctype) pair to skip it in the next step (SendBundle).
 * Output written to captured_data:
 *   per_child_contractor_report_preview: {
 *     items: [{ child_id, ctype, appraisal_no, include }, …],
 *   }
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, FileText, ExternalLink, AlertTriangle } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const pdfHref = (child_id, ctype, token) => `${BACKEND_URL}/api/appraisals/${child_id}/contractor-pdf/${encodeURIComponent(ctype)}?inline=1${token ? `&_t=${encodeURIComponent(token)}` : ''}`;

export default function PerChildContractorReportPreview({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};

  const acceptedChildren = useMemo(() => {
    const decisions = capturedData?.decisions
      || capturedData?.accepted_variants_pick?.decisions
      || capturedData?.await_quote_response?.decisions
      || capturedData?.appraisal?.child_decisions
      || capturedData?.appraisal_record?.child_decisions
      || {};
    return Object.entries(decisions)
      .filter(([, d]) => d?.decision === 'accepted')
      .map(([child_id, d]) => ({ child_id, ...d }));
  }, [capturedData]);

  const [overviews, setOverviews] = useState({}); // child_id -> overview (sections)
  const [loading, setLoading] = useState(true);
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [includeMap, setIncludeMap] = useState(() => value?.include_map || {}); // "child_id|ctype" -> bool
  const [err, setErr] = useState('');

  const loadOverviews = useCallback(async () => {
    if (!acceptedChildren.length) { setLoading(false); return; }
    setLoading(true); setErr('');
    try {
      const results = {};
      for (const c of acceptedChildren) {
        try {
          const { data } = await api.get(`/appraisals/${c.child_id}/contractor-overview`);
          results[c.child_id] = data;
        } catch (e) {
          results[c.child_id] = { error: e?.response?.data?.detail || 'Failed to load' };
        }
      }
      setOverviews(results);
    } finally { setLoading(false); }
  }, [acceptedChildren]);

  useEffect(() => { loadOverviews(); }, [loadOverviews]);

  // Compute the flat list of items = one row per (child, ctype).
  const items = useMemo(() => {
    const out = [];
    for (const c of acceptedChildren) {
      const o = overviews[c.child_id] || {};
      const sections = o.sections || [];
      if (sections.length === 0) {
        // No contractor types on this child — still surface a row so
        // the operator sees the child was checked, with a warning.
        out.push({
          child_id: c.child_id,
          appraisal_no: c.appraisal_no || o.appraisal_no,
          quote_type_ref: c.quote_type_ref,
          variant_label: c.variant_label,
          ctype: '',
          empty: true,
          include: false,
        });
      } else {
        for (const s of sections) {
          const key = `${c.child_id}|${s.contractor_type}`;
          const defaultInclude = includeMap[key] === undefined ? true : includeMap[key];
          out.push({
            child_id: c.child_id,
            appraisal_no: c.appraisal_no || o.appraisal_no,
            quote_type_ref: c.quote_type_ref,
            variant_label: c.variant_label,
            ctype: s.contractor_type,
            contractor_total: s.contractor_total,
            units_total: s.units_total,
            include: defaultInclude,
          });
        }
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptedChildren, overviews]);

  // Auto-expand the first item on load.
  useEffect(() => {
    if (items.length > 0 && expandedKeys.size === 0) {
      const first = items.find(i => !i.empty);
      if (first) setExpandedKeys(new Set([`${first.child_id}|${first.ctype}`]));
    }
  }, [items, expandedKeys]);

  const toggleExpand = (key) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleInclude = (child_id, ctype) => {
    const key = `${child_id}|${ctype}`;
    setIncludeMap(prev => ({ ...prev, [key]: prev[key] === undefined ? false : !prev[key] }));
  };

  useEffect(() => {
    if (onChange) {
      const activeItems = items.filter(i => !i.empty).map(i => ({
        child_id: i.child_id,
        ctype: i.ctype,
        appraisal_no: i.appraisal_no,
        quote_type_ref: i.quote_type_ref,
        variant_label: i.variant_label,
        include: includeMap[`${i.child_id}|${i.ctype}`] === undefined ? true : includeMap[`${i.child_id}|${i.ctype}`],
      }));
      onChange({ items: activeItems, include_map: includeMap });
    }
  }, [items, includeMap, onChange]);

  const includedCount = items.filter(i => !i.empty && (includeMap[`${i.child_id}|${i.ctype}`] === undefined ? true : includeMap[`${i.child_id}|${i.ctype}`])).length;

  return (
    <div className="space-y-4" data-testid="wf-per-child-contractor-report-preview">
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 mt-0.5 shrink-0 text-indigo-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-indigo-900">Preview Contractor Reports</div>
            <div className="text-sm mt-1 text-indigo-800">
              {params.prompt || 'One PDF per contractor per accepted quote. Expand each panel to preview. Untick any you don\'t want to send.'}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-widest text-indigo-600 font-semibold">Selected</div>
            <div className="text-lg font-bold text-indigo-900 leading-none">{includedCount}<span className="text-xs text-indigo-500 font-normal"> / {items.filter(i => !i.empty).length}</span></div>
          </div>
        </div>
      </div>

      {loading && <div className="p-6 text-sm text-slate-400 italic">Loading contractor sections…</div>}

      {!loading && items.length === 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          No accepted quotes found.
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-2">
          {items.map((it) => {
            const key = `${it.child_id}|${it.ctype || 'empty'}`;
            const isExpanded = expandedKeys.has(key);
            const include = includeMap[`${it.child_id}|${it.ctype}`] === undefined ? true : includeMap[`${it.child_id}|${it.ctype}`];
            if (it.empty) {
              return (
                <div key={key} className="p-3 border border-amber-200 bg-amber-50 rounded-lg" data-testid={`wf-preview-empty-${it.child_id}`}>
                  <div className="flex items-center gap-2 text-xs text-amber-900">
                    <AlertTriangle size={12} />
                    <b>A{it.appraisal_no}</b>
                    <span>{[it.quote_type_ref, it.variant_label].filter(Boolean).join(' · ')}</span>
                    <span className="ml-2 text-amber-700">— No contractor type assigned; this quote will be skipped.</span>
                  </div>
                </div>
              );
            }
            const label = `${it.quote_type_ref || ''}${it.variant_label ? ` · ${it.variant_label}` : ''} — ${it.ctype}`;
            const href = pdfHref(it.child_id, it.ctype, capturedData?.appraisal?.updated_at);
            return (
              <div key={key} className={`border rounded-lg ${include ? 'border-indigo-200 bg-white' : 'border-slate-200 bg-slate-50 opacity-70'}`} data-testid={`wf-preview-row-${it.child_id}-${it.ctype.replace(/\s+/g, '-')}`}>
                <div className="flex items-center justify-between gap-3 p-3">
                  <button
                    type="button"
                    onClick={() => toggleExpand(key)}
                    className="flex items-center gap-2 text-sm font-medium text-slate-800 hover:text-slate-900 min-w-0 flex-1 text-left"
                    data-testid={`wf-preview-toggle-${it.child_id}-${it.ctype.replace(/\s+/g, '-')}`}
                  >
                    <span className={`inline-block transition-transform ${isExpanded ? 'rotate-90' : ''}`}>▶</span>
                    <span className="truncate">{label}</span>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">A{it.appraisal_no}</span>
                    {it.contractor_total != null && (
                      <span className="text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">${Number(it.contractor_total).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    )}
                  </button>
                  <div className="flex items-center gap-3 shrink-0">
                    <a href={href} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1">
                      <ExternalLink size={11} /> New tab
                    </a>
                    <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                      <input
                        type="checkbox"
                        checked={include}
                        onChange={() => toggleInclude(it.child_id, it.ctype)}
                        className="rounded"
                        data-testid={`wf-preview-include-${it.child_id}-${it.ctype.replace(/\s+/g, '-')}`}
                      />
                      <span className={`font-medium ${include ? 'text-emerald-700' : 'text-slate-500'}`}>Include in send</span>
                    </label>
                  </div>
                </div>
                {isExpanded && (
                  <div className="border-t border-slate-100 p-2 bg-slate-50">
                    <iframe
                      src={href}
                      title={label}
                      className="w-full bg-white border border-slate-200 rounded"
                      style={{ height: '600px' }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-preview-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-preview-cancel">Cancel</button>}
          <button
            onClick={onNext}
            disabled={includedCount === 0}
            className="px-4 py-1.5 rounded-md text-sm bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="wf-preview-continue"
          >
            <CheckCircle2 className="w-4 h-4" /> Continue to Send ({includedCount})
          </button>
        </div>
      </div>
    </div>
  );
}
